<template>
    <div class="recursive-content">
        <template v-if="contentType === 'primitive'">
            {{ content }}
        </template>

        <template v-else-if="contentType === 'boolean'">
            <span
                v-if="content"
                class="vc-lc-square-check-big icon-lg text-success"
            />
            <span
                v-else
                class="vc-lc-square-x icon-lg text-error"
            />
        </template>

        <template v-else-if="contentType === 'array'">
            <div
                v-for="(item, index) in content"
                :key="index"
                class="array-item"
            >
                <template v-if="typeof item === 'object' && item !== null">
                    <div class="item-content">
                        <div
                            v-for="(value, key) in item"
                            :key="key"
                            class="key-value-pair"
                            :class="{ 'inline-pair': isSimpleValue(value) }"
                        >
                            <template v-if="isSimpleValue(value)">
                                <span class="key">{{ formatKey(key) }}:</span>
                                <span class="value-inline">
                                    <AnswerRecursiveContent
                                        :content="value"
                                        :is-nested="true"
                                    />
                                </span>
                            </template>
                            <template v-else>
                                <div class="key">
                                    {{ formatKey(key) }}
                                </div>
                                <div class="value-block">
                                    <AnswerRecursiveContent
                                        :content="value"
                                        :is-nested="true"
                                    />
                                </div>
                            </template>
                        </div>
                    </div>
                </template>
                <template v-else>
                    {{ item }}
                </template>
            </div>
        </template>

        <template v-else-if="contentType === 'object'">
            <div class="object-content">
                <div
                    v-for="(value, key) in content"
                    :key="key"
                    class="key-value-pair"
                    :class="{ 'inline-pair': isSimpleValue(value) }"
                >
                    <template v-if="isSimpleValue(value)">
                        <span class="key">{{ formatKey(key) }}:</span>
                        <span class="value-inline">
                            <AnswerRecursiveContent
                                :content="value"
                                :is-nested="true"
                            />
                        </span>
                    </template>
                    <template v-else>
                        <div class="key">
                            {{ formatKey(key) }}
                        </div>
                        <div class="value-block">
                            <AnswerRecursiveContent
                                :content="value"
                                :is-nested="true"
                            />
                        </div>
                    </template>
                </div>
            </div>
        </template>
    </div>
</template>


<script setup lang="ts">
import { computed } from 'vue'

/* eslint-disable @typescript-eslint/no-explicit-any */

interface Props {
    content: any
    isNested?: boolean
}

const SIMPLE_TYPE = 'primitive'
const BOOLEAN_TYPE = 'boolean'
const ARRAY_TYPE = 'array'
const OBJECT_TYPE = 'object'

const props = defineProps<Props>()


const contentType = computed(() => {
    if (typeof props.content === 'string' || typeof props.content === 'number') return SIMPLE_TYPE
    if (typeof props.content === 'boolean') return BOOLEAN_TYPE
    if (Array.isArray(props.content)) return ARRAY_TYPE
    if (typeof props.content === 'object') return OBJECT_TYPE
    return SIMPLE_TYPE
})


const formatKey = (key: string | number = '') => {
    return `${key}`.replace(/_/g, ' ')
}

const isSimpleValue = (value: any) => {
    return typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        value === null
}


</script>

<style lang="scss" scoped>
.recursive-content {
    .array-item {
        @apply py-0.5 last:mb-0;
        &:has(> .item-content) {
            @apply py-2 border-b last:border-b-0 border-ui-lines;
        }
    }

    .item-content {
        @apply ltr:pl-2 rtl:pr-2;
    }

    .key-value-pair {
        @apply mb-2 last:mb-0;

        &.inline-pair {
            @apply flex items-baseline gap-2;
        }
    }

    .key {
        @apply text-default-text font-semibold;
    }

    .value-inline {
        @apply text-default-text;
    }

    .value-block {
        @apply ltr:pl-2 rtl:pr-2 ltr:border-l rtl:border-r border-ui-lines;
    }
}
</style>



